import { useEffect, useRef } from "react";

type UseArrayListenerProps<T> = {
  array: T[];
  identifierKey: keyof T;
  onNewItem: (item: T) => void;
  enabled?: boolean; // if false, the hook won't record the array changes and will not detect new items
};

const detectNewItem = <T>(newList: T[], oldList: T[], identifierKey: keyof T) => {
  const newItems = newList.filter(
    (newItem) => !oldList.some((oldItem) => oldItem[identifierKey] === newItem[identifierKey]),
  );
  return newItems;
};

/**
 * It is used to detect new items in an array (based on an identifier key)
 * and call a callback function for each new item.
 */
export const useArrayListener = <T>({ array, identifierKey, onNewItem, enabled = false }: UseArrayListenerProps<T>) => {
  const arrayMemoryRef = useRef<T[] | null>(null);

  useEffect(() => {
    if (!enabled) {
      arrayMemoryRef.current = null;
      return;
    }

    if (!arrayMemoryRef.current) {
      arrayMemoryRef.current = array;
      return;
    }

    const newItems = detectNewItem(array, arrayMemoryRef.current, identifierKey);
    if (newItems.length) {
      newItems.forEach((item) => onNewItem(item));
    }

    arrayMemoryRef.current = array;
  }, [array, enabled]);
};
